import { t } from '@lingui/core/macro';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { type FC, useState } from 'react';

type Props = {
  initialText: string;
  initialUrl: string;
  isEditing: boolean;
  onClose: VoidFunction;
  onSubmit: (text: string, url: string) => void;
  onRemove: VoidFunction;
};

// Local validator: anchor href must carry a scheme so the rendered <a> resolves
// absolutely. `isUrlValid` from utils/validations accepts bare `www.foo.com`
// (no scheme) and rejects `mailto:`, which is the opposite of what we need
// here, so we keep a dedicated regex instead of reusing it.
const LINK_URL_REGEX = /^(https?:\/\/|mailto:)\S+/i;
const isLinkUrlValid = (url: string) => LINK_URL_REGEX.test(url.trim());

export const LinkDialog: FC<Props> = ({
  initialText,
  initialUrl,
  isEditing,
  onClose,
  onSubmit,
  onRemove,
}) => {
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);
  const hasUrl = url.length > 0;
  const isUrlValid = isLinkUrlValid(url);
  const isValid = text.trim().length > 0 && isUrlValid;
  const hasInitialText = initialText.trim().length > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(text.trim(), url.trim());
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEditing ? t`Modifier le lien` : t`Insérer un lien`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            autoFocus={!hasInitialText}
            label={t`Texte affiché`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
          />
          <TextField
            autoFocus={hasInitialText}
            label={t`URL`}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            fullWidth
            error={hasUrl && !isUrlValid}
            helperText={
              hasUrl && !isUrlValid
                ? t`URL invalide (commence par https://, http:// ou mailto:)`
                : undefined
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {isEditing && (
          <Button onClick={onRemove} color="error" sx={{ mr: 'auto' }}>
            {t`Supprimer le lien`}
          </Button>
        )}
        <Button onClick={onClose}>{t`Annuler`}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid}>
          {isEditing ? t`Modifier` : t`Insérer`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
